// Admin settings for automated email follow-up monitoring (cadence, escalation, reports).

import React, { useEffect, useState } from "react";
import { Mail, Loader2, AlertCircle, Save, LayoutDashboard } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Input, Label, Select } from "../../atoms";
import {
  useEmailFollowUpSettings,
  useUpdateEmailFollowUpSettings,
} from "../../hooks/useFollowUps";
import EmailFollowUpAdminDrawer from "../../overlays/EmailFollowUpAdminDrawer";

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

// Full IANA zone list straight from the browser, so it can never drift out of
// date. Older engines without supportedValuesOf get a short curated list.
const FALLBACK_ZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Asia/Manila",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Moscow",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Australia/Sydney", "Pacific/Auckland", "UTC",
];

// "+05:30" for a zone, at the current instant (so DST is reflected).
function offsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return name.replace("GMT", "UTC") || "UTC";
  } catch {
    return "";
  }
}

// Built once per page load — ~400 Intl calls is fine one time, not per render.
const TIMEZONE_OPTIONS = (() => {
  let zones = FALLBACK_ZONES;
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const all = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(all) && all.length) zones = [...new Set([...all, "UTC"])];
    }
  } catch {
    /* keep fallback */
  }
  return zones
    .map((value) => {
      const off = offsetLabel(value);
      return { value, label: off ? `${value} (${off})` : value, off };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
})();

const NUMBER_FIELDS = [
  { key: "firstFollowUpDays", label: "First follow-up (days)", min: 1, max: 90,
    hint: "Days after first outbound before follow-up is due." },
  { key: "subsequentFollowUpDays", label: "Next follow-up (days)", min: 1, max: 90,
    hint: "Days between subsequent outbounds if no reply." },
  { key: "dueSoonHours", label: "Due soon window (hours)", min: 1, max: 168,
    hint: "Mark as due soon this many hours before deadline." },
  { key: "escalationAfterDays", label: "Escalate after (days)", min: 0, max: 30,
    hint: "Days after a miss before escalating to manager." },
  { key: "dailyReportHour", label: "Report hour (local)", min: 0, max: 23,
    hint: "Hour to send daily/weekly reports (org timezone)." },
];

function Toggle({ checked, onChange, disabled, label, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0" }}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        style={{
          width: 40, height: 22, flexShrink: 0, borderRadius: 999, border: "none",
          background: checked ? "var(--crm-primary)" : "var(--color-border-subtle)",
          cursor: disabled ? "not-allowed" : "pointer", position: "relative",
          transition: "background 120ms ease", opacity: disabled ? 0.5 : 1,
        }}
        aria-pressed={checked}
      >
        <span style={{
          position: "absolute", top: 2, left: checked ? 20 : 2,
          width: 18, height: 18, borderRadius: 999, background: "#fff",
          transition: "left 120ms ease",
        }} />
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}

export default function EmailFollowUpSettingsSection() {
  const { data, isLoading, isError, error } = useEmailFollowUpSettings();
  const update = useUpdateEmailFollowUpSettings();
  const [form, setForm] = useState(null);
  const [monitorOpen, setMonitorOpen] = useState(false);

  useEffect(() => {
    if (data?.settings) {
      const roster = Array.isArray(data.settings.reportRoster) ? data.settings.reportRoster : [];
      setForm({
        enabled: data.settings.enabled,
        dashboardMonitorEnabled: data.settings.dashboardMonitorEnabled === true,
        autoBackfillEnabled: data.settings.autoBackfillEnabled === true,
        businessDaysOnly: data.settings.businessDaysOnly,
        pauseOnInboundReply: data.settings.pauseOnInboundReply,
        escalationEnabled: data.settings.escalationEnabled,
        dailyReportEnabled: data.settings.dailyReportEnabled,
        weeklyReportEnabled: data.settings.weeklyReportEnabled,
        firstFollowUpDays: String(data.settings.firstFollowUpDays),
        subsequentFollowUpDays: String(data.settings.subsequentFollowUpDays),
        dueSoonHours: String(data.settings.dueSoonHours),
        escalationAfterDays: String(data.settings.escalationAfterDays),
        dailyReportHour: String(data.settings.dailyReportHour),
        reportTimezone: data.settings.reportTimezone || "Asia/Kolkata",
        escalationRecipients: (data.settings.escalationRecipients || []).join(", "),
        reportProjectId: data.settings.reportProjectId || "",
        reportRosterText: roster
          .map((r) => (r.name ? `${r.name} <${r.email}>` : r.email))
          .join("\n"),
      });
    }
  }, [data?.settings]);

  const parseRosterText = (text) => {
    return String(text || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const m = line.match(/^(.+?)\s*<([^>]+)>$/);
        if (m) return { name: m[1].trim(), email: m[2].trim() };
        return { name: "", email: line };
      });
  };

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        enabled: form.enabled,
        dashboardMonitorEnabled: form.dashboardMonitorEnabled,
        autoBackfillEnabled: form.autoBackfillEnabled,
        businessDaysOnly: form.businessDaysOnly,
        pauseOnInboundReply: form.pauseOnInboundReply,
        escalationEnabled: form.escalationEnabled,
        dailyReportEnabled: form.dailyReportEnabled,
        weeklyReportEnabled: form.weeklyReportEnabled,
        firstFollowUpDays: Number(form.firstFollowUpDays),
        subsequentFollowUpDays: Number(form.subsequentFollowUpDays),
        dueSoonHours: Number(form.dueSoonHours),
        escalationAfterDays: Number(form.escalationAfterDays),
        dailyReportHour: Number(form.dailyReportHour),
        reportTimezone: form.reportTimezone,
        escalationRecipients: form.escalationRecipients
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        reportProjectId: form.reportProjectId?.trim() || null,
        reportRoster: parseRosterText(form.reportRosterText),
      });
      toast.success("Email follow-up settings updated");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 24 }}>
      <header>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--crm-primary)" }}>
          <Mail size={12} /> Email monitoring
        </div>
        <h2 style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 600 }}>Automated vendor follow-ups</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
          Tracks matched Gmail threads against lead/contact emails. No manual logging required.
        </p>
      </header>

      {isError && (
        <div style={{ fontSize: 12, color: "var(--crm-danger)", padding: 10, borderRadius: 8, background: "var(--crm-danger-soft)" }}>
          <AlertCircle size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
          {formatErr(error)}
        </div>
      )}

      {isLoading || !form ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div style={{ padding: 16, borderRadius: 8, background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)" }}>
          <Toggle checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} disabled={update.isPending}
            label="Enable email follow-up monitoring" hint="When off, obligations are not created or updated." />
          <Toggle checked={form.dashboardMonitorEnabled} onChange={(v) => setForm((f) => ({ ...f, dashboardMonitorEnabled: v }))} disabled={update.isPending || !form.enabled}
            label="Show follow-up monitor on dashboard" hint="org_admin only. Adds a dashboard button that opens the team follow-up drawer. Default off." />
          {form.dashboardMonitorEnabled && (
            <div style={{ padding: "8px 0 4px" }}>
              <Button variant="secondary" size="sm" leftIcon={<LayoutDashboard size={13} />} onClick={() => setMonitorOpen(true)}>
                Open follow-up monitor
              </Button>
            </div>
          )}
          <div style={{ borderTop: "1px solid var(--color-border-subtle)", margin: "8px 0" }} />
          <Toggle checked={form.autoBackfillEnabled} onChange={(v) => setForm((f) => ({ ...f, autoBackfillEnabled: v }))} disabled={update.isPending || !form.enabled}
            label="Auto-index existing email history"
            hint="Sweeps each lead's past Gmail threads in the background so monitoring sees conversations that started before the mailbox was connected. Runs once per lead, a few leads at a time. Off by default." />
          <Toggle checked={form.businessDaysOnly} onChange={(v) => setForm((f) => ({ ...f, businessDaysOnly: v }))} disabled={update.isPending || !form.enabled}
            label="Business days only" hint="Cadence skips weekends." />
          <Toggle checked={form.pauseOnInboundReply} onChange={(v) => setForm((f) => ({ ...f, pauseOnInboundReply: v }))} disabled={update.isPending || !form.enabled}
            label="Pause on vendor reply" hint="Reset follow-up clock when vendor replies." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            {NUMBER_FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input type="number" value={form[f.key]} min={f.min} max={f.max} disabled={update.isPending || !form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))} />
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>{f.hint}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Label>Report timezone</Label>
            <Select
              showSearch
              value={form.reportTimezone}
              disabled={update.isPending || !form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, reportTimezone: e.target.value }))}
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </Select>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
              Drives report send time, the “due today” cutoff, and which days count as weekends.
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Label>Escalation & report recipients (comma-separated emails)</Label>
            <Input value={form.escalationRecipients} disabled={update.isPending || !form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, escalationRecipients: e.target.value }))}
              placeholder="suparna@example.com" />
          </div>
          <div style={{ marginTop: 12 }}>
            <Label>Report project ID (optional)</Label>
            <Input value={form.reportProjectId} disabled={update.isPending || !form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, reportProjectId: e.target.value }))}
              placeholder="CRM project ObjectId — leave blank for whole org" />
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
              Pin daily/weekly digest + Excel to one project (e.g. VirVentures Inc).
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Label>Report roster (one per line: Name &lt;email&gt; or email)</Label>
            <textarea
              value={form.reportRosterText}
              disabled={update.isPending || !form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, reportRosterText: e.target.value }))}
              rows={8}
              placeholder={"Anupam Muhuri <anupam.m@virventures.com>\nteam@ergode.com"}
              style={{
                width: "100%", boxSizing: "border-box", padding: 10, borderRadius: 8,
                border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-base)",
                fontSize: 13, fontFamily: "inherit", resize: "vertical",
                color: "var(--color-text-primary)",
              }}
            />
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
              When set, the digest always includes these VRMs (zeros allowed) and attaches Excel.
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--color-border-subtle)", margin: "12px 0" }} />
          <Toggle checked={form.escalationEnabled} onChange={(v) => setForm((f) => ({ ...f, escalationEnabled: v }))} disabled={update.isPending || !form.enabled}
            label="Enable escalation" />
          <Toggle checked={form.dailyReportEnabled} onChange={(v) => setForm((f) => ({ ...f, dailyReportEnabled: v }))} disabled={update.isPending || !form.enabled}
            label="Daily report email" />
          <Toggle checked={form.weeklyReportEnabled} onChange={(v) => setForm((f) => ({ ...f, weeklyReportEnabled: v }))} disabled={update.isPending || !form.enabled}
            label="Weekly report email (Mondays)" />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <Button variant="primary" size="sm" leftIcon={update.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              onClick={handleSave} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save email settings"}
            </Button>
          </div>
        </div>
      )}
      <EmailFollowUpAdminDrawer open={monitorOpen} onClose={() => setMonitorOpen(false)} />
    </section>
  );
}
